<template>
  <div class="relative flex h-full shadow-top bg-base-0 editor-header">
    <div class="editor-header-content">
      <div
        class="editor-header-left-side"
      >
        <EditorHeaderLogo />

        <!-- Edit name, private mode & sync loading -->
        <EditorHeaderPresentationName
          @updatePresentation="updatePresentation"
          :isSwitchingPresentationMode="isSwitchingPresentationMode"
        />

        <EditorHeaderPrivateModeSetting
          @switchPrivateMode="switchPrivateMode"
          :isSwitchingPresentationMode="isSwitchingPresentationMode"
        />

        <SyncLoading v-if="!isBackstage" />

        <div class="editor-header-line" v-if="!isBackstage">
          <svg
            width="3"
            height="29"
            viewBox="0 0 1 29"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <line
              x1="0.5"
              y1="2.18557e-08"
              x2="0.499999"
              y2="29"
              stroke="#D3D7E1"
            />
          </svg>
        </div>

        <ControlActionHistories v-if="!isBackstage"></ControlActionHistories>

        <div class="editor-header-right-side">
          <span
            class="actions-kebab-menu"
            @click="toggleSlideUp('actions')"
          >
            <aha-icon name="kebab-menu" />
          </span>

          <EditorHeaderMobileSlideUpMenu
            @switchPrivateMode="switchPrivateMode"
            @showSetting="showSetting()"
            @showExport="showExport()"
            @showShare="showShare()"
            @showShareToEdit="isShareToEditDialogShowing = true"
          />

          <EditorHeaderHomeButton v-if="isPresenterWhiteLabel && !isBackstage" />

          <OnlineCollaboratorsList
            :list="presentationEditors"
            @openShareToEditPopup="handleOpenShareToEditPopup"
            v-if="isOnlineCollabListVisible && !isBackstage"
          >
          </OnlineCollaboratorsList>
          <EditorHeaderHelpCenter v-if="!isBackstage" />
          <EditorHeaderShareButton v-if="!isBackstage" @showShare="showShare()" />

          <EditorHeaderButtonsGroup
            v-if="!isBackstage"
            @showSetting="showSetting()"
            @showExport="showExport()"
            :waitPresenting="waitPresenting"
            @togglePresentOptions="togglePresentOptions()"
            @startPresentingMode="startPresentingMode"
          />

          <EditorHeaderPresentButton
            @togglePresentOptions="togglePresentOptions"
            @handlePresentMenuClick="handlePresentMenuClick"
            :showPresentOptions="showPresentOptions"
          />

          <!-- Present button mobile-->
          <span class="inline sm:hidden relative -top-0.5">
            <aha-button
              class="min-w-0"
              :disabled="isNewPresentation || isDisableEditPresentation"
              @click="toggleSlideUp('present')"
              @mouseup.stop
            >
              <aha-icon
                name="play"
                viewBox="0 0 24 24"
                width="15"
                height="15"
                v-if="!waitPresenting"
              />
              <aha-spinner variant="white" v-else />
            </aha-button>
          </span>

          <!-- Slide Up for Present Button on mobile -->
          <EditorHeaderMobilePresentButton
            v-if="currentUser.rememberSettings"
            @handlePresentMenuClick="handlePresentMenuClick"
            @handleOpenBackstageWindow="_handleOpenBackstageWindow"
          />
        </div>

        <ModalConfirmPresentingNoLeaderboard
          v-if="isShowConfirmQuizPresentModal"
          @hideModal="isShowConfirmQuizPresentModal = false"
          @addQuiz="addQuiz()"
          @hideModalAndMaybePresent="hideModalAndMaybePresent(modalTypes.noLeaderBoard)"
        />

        <!-- Show when presenter click on Present but still in private mode -->
        <ModalEditorHeaderPrivateModeWarning
          v-if="isShowPrivateModeConfirmModal"
          @hideModal="isShowPrivateModeConfirmModal = false"
          :isShowCorrectAnswerReminder="_isShowCorrectAnswerReminder"
          :isAudiencePacing="_isAudiencePacing"
          @setScreenMode="_setScreenMode"
          @changeRouterIfPresenting="_changeRouterIfPresenting"
          @startPresenting="_startPresenting"
        />

        <!-- Presenter click on Present but not check correct answer pick answer + pick img -->
        <ModalQuizNoCorrectAnswer
          type="quiz-no-correct-answer"
          :data="dataConfirm"
          @present="handleClickPresentAnyway(modalTypes.noCorrectAnswer)"
          @hide="hideModalAndMaybePresent(modalTypes.noCorrectAnswer)"
          v-if="isShowingModalQuizNoCorrectAnswer"
        />

        <ModalAudiencePacing
          @hide="hideModalAndMaybePresent(modalTypes.audiencePacing)"
          @present="handleClickPresentAnyway(modalTypes.audiencePacing)"
          v-if="isShowingModalAudiencePacing"
        />

        <ShareToEditModal
          v-if="isShareToEditDialogShowing && workingMode === 'personal'"
          :presentation="presentation"
          @hide="isShareToEditDialogShowing = false"
        />
        <ShareToTeamForEditModal
          :teamName="currentAccount.name"
          v-if="isShareToEditDialogShowing && workingMode === 'team'"
          @hide="isShareToEditDialogShowing = false"
        />
      </div>

      <div
        class="editor-header-tab-container"
        v-if="isShowSetting
          || isShowShareTab
          || isShowResult"
      >
        <!-- Top header for mobile when open settings/share/results tab -->
        <div
          v-if="!isShowTeamSetup || !isShowReport"
          class="sticky top-0 z-20 flex items-center justify-center h-16 font-bold shadow-top bg-base-0 text-base-90 md:hidden"
        >
          <span class="absolute cursor-pointer left-4 top-6" @click="handleBackDropClick">
            <aha-icon
              name="arrow-left"
              height="30"
              width="30"
              viewBox="0 0 30 30"
            />
          </span>

          <aha-icon
            :name="tabContent.icon"
            class="mr-2 text-base-70"
          />
          {{ tabContent.title }}
        </div>

        <!-- actual tab content section -->
        <Settings
          v-show="isShowSetting"
          :default-expanded-accordion="defaultExpandedAccordion"
          @handle-hide-main-header="handleHideMainHeader()"
        />

        <Results
          v-if="isShowResult"
          @handle-hide-main-header="handleHideMainHeader()"
        />

        <Share
          v-if="isShowShareTab"
          :presentation="presentation"
          :isShowShareACopyList="isShowShareACopyList"
          :handleHideMainHeader="handleHideMainHeader"
          :handleShowSendACopyDialog="() => isShowShareACopyList = true"
          :default-expanded-accordion="defaultExpandedAccordionShare"
        />
      </div>

      <transition name="fade">
        <div
          @click="handleBackDropClick"
          class="fixed inset-0 z-10 hidden bg-base-100 opacity-20 md:block"
          v-if="isShowSetting
            || isShowShareTab
            || isShowResult
            || showPresentOptions
          "
        />
      </transition>
    </div>
  </div>
</template>
<script>
import screenfull from 'screenfull'
// import { pages, app } from '@microsoft/teams-js'
import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import Config from '@/config'
import Utils from '@/util'
import EventBus from '@/eventBus'
import * as localStorageExpiry from '@/utils/local.storage.expiry'
import constants from '@/constant'
// api
import PresentationApi from '@/api/presentation'

import gaCategories from '@/constant/gaCategories'
import { SET_CURRENT_SLIDE_MODE, FULL_SCREEN_MODE, WINDOWED_MODE } from '@/vuex/types'

import ShareToEditModal from '@/components-v2/modals/ShareToEditModal.vue'
import ShareToTeamForEditModal from '@/components-v2/modals/ShareToTeamForEdit.vue'
import Results from '@/components-v2/presentation-editor/editor-header/Results'
import Settings from '@/components-v2/presentation-editor/editor-header/Settings.vue'
import Share from '@/components-v2/presentation-editor/editor-header/Share.vue'
import OnlineCollaboratorsList from '@/components-v2/presentation-editor/editor-header/OnlineCollaboratorsList.vue'
import SyncLoading from '@/components-v2/presentation-editor/editor-header/SyncLoading'
import EditorHeaderLogo from '@/components-v2/presentation-editor/editor-header/EditorHeaderLogo.vue'
import EditorHeaderPresentationName from '@/components-v2/presentation-editor/editor-header/EditorHeaderPresentationName.vue'
import EditorHeaderShareButton from '@/components-v2/presentation-editor/editor-header/EditorHeaderShareButton.vue'
import EditorHeaderHelpCenter from '@/components-v2/presentation-editor/editor-header/EditorHeaderHelpCenter.vue'
import EditorHeaderButtonsGroup from '@/components-v2/presentation-editor/editor-header/EditorHeaderButtonsGroup.vue'
import EditorHeaderPresentButton from '@/components-v2/presentation-editor/editor-header/EditorHeaderPresentButton.vue'
import EditorHeaderMobilePresentButton from '@/components-v2/presentation-editor/editor-header/EditorHeaderMobilePresentButton.vue'
import EditorHeaderMobileSlideUpMenu from '@/components-v2/presentation-editor/editor-header/EditorHeaderMobileSlideUpMenu.vue'
import EditorHeaderPrivateModeSetting from '@/components-v2/presentation-editor/editor-header/EditorHeaderPrivateModeSetting.vue'
import EditorHeaderHomeButton from '@/components-v2/presentation-editor/editor-header/EditorHeaderHomeButton.vue'
import ModalEditorHeaderPrivateModeWarning from '@/components-v2/modals/EditorHeaderPrivateModeWarning.vue'
import ModalConfirmPresentingNoLeaderboard from '@/components-v2/modals/EditorHeaderPresentingNoLeaderboard.vue'

import ModalQuizNoCorrectAnswer from '../../modals/QuizNoCorrectAnswer.vue'
import ModalAudiencePacing from '../../modals/AudiencePacing.vue'
import mixpanelUtils from '@/utils/mixpanel.utils'
import ControlActionHistories from '@/components-v2/presentation-editor/editor-header/ControlActionHistories'

const modalTypes = {
  noLeaderBoard: 1,
  privateMode: 2,
  noCorrectAnswer: 3,
  audiencePacing: 4,
}

export default {
  components: {
    ModalQuizNoCorrectAnswer,
    ModalAudiencePacing,
    Settings,
    Share,
    Results,
    OnlineCollaboratorsList,
    SyncLoading,
    ControlActionHistories,
    EditorHeaderLogo,
    EditorHeaderPresentationName,
    EditorHeaderShareButton,
    EditorHeaderHelpCenter,
    EditorHeaderButtonsGroup,
    EditorHeaderPresentButton,
    EditorHeaderMobilePresentButton,
    EditorHeaderMobileSlideUpMenu,
    EditorHeaderPrivateModeSetting,
    ModalEditorHeaderPrivateModeWarning,
    ModalConfirmPresentingNoLeaderboard,
    EditorHeaderHomeButton,
    ShareToEditModal,
    ShareToTeamForEditModal,
  },
  data() {
    return {
      waitPresenting: false,
      isSwitchingPresentationMode: false,
      isShowPrivateModeConfirmModal: false,
      isShowConfirmQuizPresentModal: false,
      isShowingModalQuizNoCorrectAnswer: false,
      isShowingModalAudiencePacing: false,
      isShareToEditDialogShowing: false,
      isShowTeamSetup: false,
      isShowReport: false,
      showPresentOptions: false,
      isSlideUpVisible: false,
      isSlideUpPresentVisible: false,
      dataConfirm: {},
      defaultExpandedAccordion: '',
      defaultExpandedAccordionShare: '',
      modalTypes,
      isShowShareACopyList: false,
      backStageWindow: null,
      // onlineCollaboratorsList: fixtures.onlineCollaboratorsList,
      clickedOnPresentWithBackstage: false,
    }
  },

  computed: {
    ...mapGetters(['isBackstage']),
    ...mapState({
      isPresenterWhiteLabel: (state) => state.isPresenterWhiteLabel,
      isNewPresentation: (state) => state.isNewPresentation,
      isGuestUser: (state) => state.currentUser.isGuest,
      presentation: (state) => state.presentation,
      sharePresentation: (state) => state.sharePresentation,
      presentationEditors: (state) => state.presentationEditors,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      currentUser: (state) => state.currentUser,
      currentAccount: (state) => state.currentAccount,
      slideActive: (state) => state.slideActive,
      workingMode: (state) => state.workingMode,
    }),
    isShowSetting() {
      return this.presentation.id && this.presentation.showSetting
    },
    isShowShareTab() {
      return this.presentation.id && this.presentation.showShare
    },
    isShowResult() {
      return this.presentation.id && this.presentation.showExport
    },
    tabContent() {
      const contentMap = {}
      if (this.isShowSetting) {
        contentMap.title = 'Settings'
        contentMap.icon = 'setting'
      }
      if (this.isShowShareTab) {
        contentMap.title = 'Share'
        contentMap.icon = 'share'
      }
      if (this.isShowResult) {
        contentMap.title = 'Results'
        contentMap.icon = 'download'
      }
      return contentMap
    },
    hasNoCode() {
      return !this.presentation.qrcode
        || !this.presentation.shareCode
        || !this.presentation.uniqueAccessCode
    },
    isOnlineCollabListVisible() {
      return this.currentUser.identifyProvider !== 'skoletube'
    },
    qrCodeKey() {
      return `closeAutoOpenQRCode-prez${this.presentation.id}`
    },

  },
  mounted() {
    const { presenting, openRequestPresentation } = this.$route.query
    EventBus.$on('show-setting-popup', ({ defaultExpandedAccordion }) => {
      this.showSetting({ defaultExpandedAccordion })
    })
    EventBus.$on('open-backstage-window', this._handleOpenBackstageWindow)
    EventBus.$on('close-backstage-window', this.closeBackstageWindow)
    EventBus.$on('present-with-backstage', () => {
      this.handlePresentMenuClick('present-with-backstage')
    })

    if (presenting && !this.sharePresentation) {
      this._setScreenMode()
    }
    if (this.sharePresentation) {
      this.$store.dispatch(SET_CURRENT_SLIDE_MODE, WINDOWED_MODE)
      window.dispatchEvent(new Event('resize'))
    }
    this.handleTriggerOpenReport()
    this.handleTriggerFromQueryParams()
    window.addEventListener('unload', () => {
      if (this.isBackstage && localStorage) {
        localStorage.removeItem(`Backstage_${this.presentation.id}`)
      }
    })
    if (openRequestPresentation) {
      this.showShare()
      this.defaultExpandedAccordionShare = 'shareToCopy'
      this.$router.replace({ query: {} })
    }
  },
  destroyed() {
    EventBus.$off('show-setting-popup')
    EventBus.$off('open-backstage-window')
    EventBus.$off('close-backstage-window')
    EventBus.$off('present-with-backstage')
  },
  methods: {
    ...mapActions(['patchPresentationAction', 'updateSlideActiveAction', 'setSlideActiveAction', 'setSyncLoadingAction']),
    hasClosedQRCode() {
      return localStorageExpiry.getWithExpiry(this.qrCodeKey)
    },
    handleTriggerOpenReport() {
      const reportQueryParam = Utils.getParamValue('report')
      if (reportQueryParam) {
        this.showExport()
      }
    },
    handleTriggerFromQueryParams() {
      const shouldOpenExport = Utils.getParamValue('export')
      if (shouldOpenExport) {
        this.showExport()
      }
    },
    handleHideMainHeader() {
      this.isShowTeamSetup = !this.isShowTeamSetup
      this.isShowShareACopyList = !this.isShowShareACopyList
      this.isShowReport = !this.isShowReport
    },

    togglePresentOptions() {
      if (this.isNewPresentation || this.isDisableEditPresentation) {
        return
      }
      this.showPresentOptions = !this.showPresentOptions
    },

    // handle close context-menu and slide-up when clicking the item inside
    handlePresentMenuClick(type, isSlideup) {
      const eventAction = 'start presenting'
      const customProps = {
        ...mixpanelUtils.getPresentationContext(this.presentation),
        ...Utils.getSlideCountObject(this.presentation),
      }
      if (type === 'present-now') {
        this.startPresentingMode(true)
        Utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          eventAction,
          'Present Dropdown Menu > Present now',
          this.presentation.id,
          customProps,
        )
      }
      if (type === 'present-from-beginning') {
        this.startPresentingMode(true, true)
        Utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          eventAction,
          'Present Dropdown Menu > Present from beginning',
          this.presentation.id,
          mixpanelUtils.getPresentationContext(this.presentation),
        )
      }
      if (type === 'present-with-backstage') {
        this.clickedOnPresentWithBackstage = true
        this.startPresentingMode(true, false, 'present-with-backstage')
        Utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          eventAction,
          'Present Dropdown Menu > Present with Backstage',
          this.presentation.id,
          mixpanelUtils.getPresentationContext(this.presentation),
        )
      }
      if (isSlideup) {
        this.toggleSlideUp('present')
      } else {
        this.togglePresentOptions()
      }
      this.showPresentOptions = false
    },

    toggleSlideUp(type) {
      if (type === 'actions') {
        EventBus.$emit('show-mobile-slide-up-menu')
      }
      if (type === 'present') {
        EventBus.$emit('show-mobile-present-btn')
      }
    },

    handleBackDropClick() {
      this.$store.state.presentation.showSetting = false
      this.$store.state.presentation.showShare = false
      this.$store.state.presentation.showExport = false
      this.isShowShareACopyList = false
      this.showPresentOptions = false
    },

    _setScrollClass(settingOn) {
      if (settingOn) {
        document.body.classList.add('no-scroll')
      } else {
        document.body.classList.remove('no-scroll')
      }
    },

    showSetting(config) {
      if (config) {
        this.defaultExpandedAccordion = config.defaultExpandedAccordion || ''
      }
      this.patchPresentationAction({
        showSetting: true,
        showExport: false,
        showShare: false,
      })
      this._setScrollClass(this.presentation.showSetting)

      if (this.$store.state.presentation.showSetting) {
        Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Editor Header > Open settings menu')
      } else {
        Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Editor Header > Close settings menu')
      }
    },
    async showShare() {
      this.patchPresentationAction({
        showShare: true,
        showSetting: false,
        showExport: false,
      })
      this._setScrollClass(this.presentation.showShare)
      if (this.presentation.showShare) {
        Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Open Share menu')
      } else {
        Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Editor Header > Close invite menu')
      }

      if (this.hasNoCode) {
        try {
          const res = await PresentationApi.getPresentationBasic(this.presentation.id)
          this.patchPresentationAction({
            qrcode: res.qrcode,
            shareCode: res.shareCode,
            uniqueAccessCode: res.uniqueAccessCode,
          })
        } catch (error) {
          Sentry.captureException(error, 'getPresentationBasic failed.')
        }
      }
    },
    showExport() {
      this.patchPresentationAction({
        showExport: true,
        showShare: false,
        showSetting: false,
      })
      this._setScrollClass(this.presentation.showExport)

      if (this.presentation.showExport) {
        Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Editor Header > Open results menu')
      } else {
        Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Editor Header > Close results menu')
      }
    },

    async updatePresentation(payload) {
      this.$store.dispatch('setSyncPresentation', 1)
      let res

      try {
        res = await this.$http.put(Config.api.updatePresentation + this.$route.params.id, payload)
      } catch (error) {
        this.$store.dispatch('setSyncPresentation', 3)
        throw error
      }

      if (payload.name) {
        const [, changedRow] = res.body
        const { name, slugifiedName } = changedRow[0]
        this.patchPresentationAction({
          name,
          slugifiedName,
        })
        Utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          'Editor Header > Presentation Name > Submit',
        )
      }

      this.$store.dispatch('setSyncPresentation', 2)
    },

    async switchPrivateMode() {
      if (this.isDisableEditPresentation) return
      this.isSwitchingPresentationMode = true
      const presentationId = this.$route.params.id
      const payload = { privateMode: !this.$store.state.presentation.privateMode }

      try {
        await this.$http.put(Config.api.updatePresentation + presentationId, payload)
      } catch (error) {
        Utils.showToastError('Changing status failed. Please try again.')
        Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Editor Header > Private mode setting > Presenter changed Private Mode failed', JSON.stringify(error))
        throw error
      } finally {
        this.isSwitchingPresentationMode = false
      }

      this.$store.state.presentation.privateMode = payload.privateMode
      Utils.showToastSuccess(`Presentation has been switched to ${this._setPrivateModeStatus()}.`)
      Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Editor Header > Private mode setting > Success', `Switch to ${this._setPrivateModeStatus()}`)
    },

    _setPrivateModeStatus() {
      if (this.$store.state.presentation.privateMode) {
        return 'private'
      }
      return 'public'
    },

    async startPresentingMode(isExtraButton, isBeginning, presentingType) {
      if (this.$store.state.isNewPresentation || this.isDisableEditPresentation) {
        return
      }
      this.$store.state.presentIsBeginning = isBeginning
      this.$store.state.presentIsExtraButton = isExtraButton

      if (this.checkLeaderboard()) {
        this.isShowConfirmQuizPresentModal = true
        return
      }
      if (this._checkShowPrivateModeModal()) return
      if (this._isShowCorrectAnswerReminder()) return
      if (this._isAudiencePacing()) return
      if (presentingType === 'present-with-backstage') {
        this._handleOpenBackstageWindow()
      }
      this._startPresenting()
      this.showPresentOptions = false
      localStorage.removeItem('showed_result_modal')
    },

    hasBackstageWindow() {
      return this.backStageWindow && JSON.parse(localStorage.getItem(`Backstage_${this.presentation.id}`) || false)
    },

    _isAudiencePacing() {
      if (this.$store.state.presentation.audiencePacing) {
        this.isShowingModalAudiencePacing = true
        return true
      }
      return false
    },

    _checkShowPrivateModeModal() {
      if (this.$store.state.presentation.privateMode) {
        this.isShowPrivateModeConfirmModal = true
        return true
      }
      return false
    },

    _isShowCorrectAnswerReminder() {
      if (this.slideActive.slideType === 'matchPairs') return false

      const numberOfSlideNotCheck = this.presentation.quizNoCorrectAnswer.length
      if ((numberOfSlideNotCheck > 0 && this.presentation.notRemindCorrectAnswer)
       || numberOfSlideNotCheck === 0) {
        return false
      }
      this._sendDataConfirmToReminderModal()
      return true
    },

    _startPresenting() {
      if (this.clickedOnPresentWithBackstage) {
        this._handleOpenBackstageWindow()
      }
      this.waitPresenting = true
      this.updatePresentationByType('startPresenting')
    },

    updatePresentationByType(type) {
      if (this.$store.state.presentIsBeginning) {
        const firstSlide = this.presentation.slides[0]
        if (firstSlide.id) {
          this.updateSlideActiveAction({
            activeTab: null,
            id: null,
          })
          this.setSlideActiveAction(firstSlide)
          EventBus.$emit('eventHandleSetValueTransition')
          EventBus.$emit('updateNextSlide', firstSlide.id)
        }
      }

      let payload = {}

      if (type === 'startPresenting') {
        payload = {
          presenting: true,
          activeSlidePreseting: this.$store.state.slideActive.id,
          activeSlideAccessCode: this.$store.state.presentation.uniqueAccessCode,
        }
      } else if (type === 'notRemindCorrectAnswer') {
        payload = {
          notRemindCorrectAnswer: this.$store.state.presentation.notRemindCorrectAnswer,
        }
      }
      this._updatePresentationByType(type, payload)
    },

    async _updatePresentationByType(type, payload) {
      try {
        await PresentationApi.updatePresentationByPresentationId(this.presentation.id, payload)
        if (type === 'startPresenting') {
          this._handleAfterUpdatePresentationByType()
        }
      } catch (error) {
        this.setSyncLoadingAction(3)
      }
    },

    _handleAfterUpdatePresentationByType() {
      this.waitPresenting = false
      this.$store.state.showLiveAudienceDemo = false

      // AHA-890 Remember fullscreen setting
      if (this.$store.state.presentIsExtraButton) {
        if (this.$store.state.presentIsBeginning) {
          Utils.gaTrackEvent(
            gaCategories.PRESENTATION_EDITOR,
            'Editor Header > Extra Present button > Present from beginning',
            `Presentation: ${this.presentation.id}`,
          )
        } else {
          Utils.gaTrackEvent(
            gaCategories.PRESENTATION_EDITOR,
            'Editor Header > Extra Present button > Present now',
            `Presentation: ${this.presentation.id}`,
          )
        }
      } else {
        Utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          'Editor Header > Main Present button',
          `Presentation: ${this.presentation.id}`,
        )
      }
      if (!this.hasClosedQRCode()) {
        setTimeout(() => {
          this.$store.state.isQRCodeVisible = true
          Utils.gaTrackEvent(gaCategories.PRESENTING_MODE, 'QR code auto enable', 'Open QR code', this.slideActive.id, {
            slideIndex: this.slideActive.index,
          })
        }, 1000)
      }
      this._setScreenMode()
      this._changeRouterIfPresenting()
      this.updateOnlineCount()
      if (this.slideActive.type === 'pickAnswer') {
        this._getAudienceOnlineForQuiz()
      }
    },

    updateOnlineCount() {
      const userOnlineCount = this.$store.state.userOnline
      setTimeout(() => {
        this.$emit('update-online-count', this.presentation.id, userOnlineCount)
      }, 10000)
    },

    _getAudienceOnlineForQuiz() {
      PresentationApi.getAudienceOnline({
        accessCode: this.presentation.uniqueAccessCode,
      })
    },

    async _setScreenMode() {
      if (this.clickedOnPresentWithBackstage) {
        this._handleOpenBackstageWindow()
      }
      // AHA-890 Remember fullscreen setting
      if (this.$store.state.currentUser.rememberSettings
            && this.$store.state.currentUser.rememberSettings.fullscreen
            && !this.$store.state.isPlayDemoMode) {
        try {
          this.$store.dispatch(SET_CURRENT_SLIDE_MODE, FULL_SCREEN_MODE)
          screenfull.request()
        } catch {
          this.$store.dispatch(SET_CURRENT_SLIDE_MODE, WINDOWED_MODE)
          window.dispatchEvent(new Event('resize'))
        }
      } else {
        this.$store.dispatch(SET_CURRENT_SLIDE_MODE, WINDOWED_MODE)
        window.dispatchEvent(new Event('resize'))
      }
    },

    _changeRouterIfPresenting() {
      if (window.innerWidth > 800) {
        this.$router.replace({
          name: 'PresentationDetail',
          params: { id: this.$store.state.presentation.id },
          query: { presenting: true },
        })
      }
    },

    checkLeaderboard() {
      const { slides } = this.$store.state.presentation
      let indexQuiz = 0
      const slidesQuiz = slides.filter((item) => {
        return item.type === 'pickAnswer'
      })
      const slidesLeaderboard = slides.filter((item) => {
        return item.type === 'leaderboard'
      })
      if (slidesQuiz.length) {
        indexQuiz = Utils.getIndexSlide(
          slides,
          slidesQuiz[slidesQuiz.length - 1].id,
        )
      } else {
        return false
      }

      if (!slidesLeaderboard.length) {
        return true
      }

      const index = Utils.getIndexSlide(
        slides,
        slidesLeaderboard[slidesLeaderboard.length - 1].id,
      )

      if (index > indexQuiz) {
        return false
      }
      return true
    },

    hideModalAndMaybePresent(type) {
      if (type === modalTypes.noLeaderBoard) {
        this.isShowConfirmQuizPresentModal = false
        if (this._checkShowPrivateModeModal()) return
        if (this._isShowCorrectAnswerReminder()) return
        if (this._isAudiencePacing()) return
      } else if (type === modalTypes.noCorrectAnswer) {
        this.$store.state.presentation.notRemindCorrectAnswer = false
        this.isShowingModalQuizNoCorrectAnswer = false
        return
      } else if (type === modalTypes.audiencePacing) {
        this.isShowingModalAudiencePacing = false
        return
      }
      this._startPresenting()
    },
    addQuiz() {
      this.isShowConfirmQuizPresentModal = false
      EventBus.$emit('addSlideLeaderboard', {
        lastSlide: true,
      })
      if (this._checkShowPrivateModeModal()) return
      this._isShowCorrectAnswerReminder()
    },

    _sendDataConfirmToReminderModal() {
      this._setIndexSlide()
      this._setContentForReminderModal()
      this._trackGAForReminderModal()
      this.isShowingModalQuizNoCorrectAnswer = true
    },

    _setIndexSlide() {
      this.presentation.quizNoCorrectAnswer.forEach((slide) => {
        slide.index = Utils.getIndexSlide(this.$store.state.presentation.slides, slide.id) + 1
      })
    },

    _setContentForReminderModal() {
      if (this.presentation.quizNoCorrectAnswer.length === 1) {
        this.dataConfirm = {
          title: 'Quiz slide having no correct answers?',
          description: "You haven't specified the correct answer for this Quiz question:",
        }
      } else {
        this.dataConfirm = {
          title: 'Quiz slides having no correct answers?',
          description: `You haven't specified the correct answers for these ${this.presentation.quizNoCorrectAnswer.length} Quiz questions:`,
        }
      }
    },

    _trackGAForReminderModal() {
      Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Editor Header > Present Button > Modal unchecked correct answer reminder pops up', `Presentation id: ${this.$store.state.presentation.id}`)
    },

    handleClickPresentAnyway(type) {
      if (type === modalTypes.noCorrectAnswer) {
        if (this.$store.state.presentation.notRemindCorrectAnswer) {
          this.updatePresentationByType('notRemindCorrectAnswer')
        }
        this.isShowingModalQuizNoCorrectAnswer = false
        if (this._isAudiencePacing()) return
      }
      if (type === modalTypes.audiencePacing) {
        this.isShowingModalAudiencePacing = false
      }
      this._startPresenting()
    },

    handleOpenShareToEditPopup() {
      if (this.isGuestUser) {
        // On demo mode, open Sign up page
        this.$router.push({
          name: 'Register',
          query: {
            source: 'demo',
            medium: 'demo',
            campaign: 'demo',
            content: 'share-button',
          },
        })
      } else {
        this.isShareToEditDialogShowing = true
      }
      const isSelfCreatedTeam = this.$store.state.workingMode === constants.workspace.PERSONAL ?
        null : (
          this.$store.state.currentAccount.createdFrom === 'presenter'
        )
      const teamId = this.$store.state.workingMode === constants.workspace.PERSONAL ?
        null : (
          this.$store.state.currentAccount.id
        )
      const presentationId = this.$store.state.presentation.id
      Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR,
        'Header > Open Invite to edit Popup',
        `Presentation id: ${this.$store.state.presentation.id}`,
        '', {
          isSelfCreatedTeam,
          teamId,
          presentationId,
        })
    },

    _handleOpenBackstageWindow(source) {
      this.clickedOnPresentWithBackstage = false
      if (!this.hasBackstageWindow()) {
        this.backStageWindow = window.open(`/presentation/${this.presentation.id}/backstage`, `Backstage_${this.presentation.id}`, `width=${window.outerWidth - 240},height=${window.outerHeight - 180}`)
        localStorage.setItem(`Backstage_${this.presentation.id}`, JSON.stringify(true))
      } else {
        this.backStageWindow.focus()
      }
      if (source === 'from-context-menu') return
      this._trackingBackstage()
    },

    closeBackstageWindow() {
      if (localStorage) {
        localStorage.removeItem(`Backstage_${this.presentation.id}`)
      }

      if (this.backStageWindow) {
        this.backStageWindow.close()
        this.backStageWindow = null
      }
      if (this.isBackstage) {
        window.close()
      }
    },

    _trackingBackstage() {
      Utils.gaTrackEvent(
        gaCategories.PRESENTATION_EDITOR,
        'Header > Presenting menu > Backstage',
        this.presentation.id,
      )
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss-v2/variables.scss";

.editor-header-tab-container {
  @apply bg-base-0 w-full;
  @apply right-0 overflow-y-auto z-20;
  @apply h-full fixed;
  @apply md:absolute md:top-15;
  @media (min-width: 768px) { // md screens
    height: calc(100vh - 3.75rem); // header height is 4rem
    width: $rightSideWidth;
  }
}

.editor-header-content {
  @apply flex justify-around w-full pr-4;
}

.editor-header-left-side {
  @apply flex flex-1 items-center justify-start flex-shrink-0;
  @media (max-width: 768px) {
    &::v-deep {
      .sync-error {
        top: 30px;
        left: 90%;
        .arrow-box::before {
          top: -11px;
          left: 48%;
        }
        .arrow-box::after {
          top: -10px;
          left: 48%;
        }
      }
    }
  }
}

.editor-header-right-side {
  @apply flex items-center justify-end flex-shrink-0 ml-auto;
}

.actions-kebab-menu {
  @apply cursor-pointer inline-block md:hidden -mt-0.25 p-2;
  @apply top-18 absolute right-4;
}

@media (max-width: 768px) {
  .editor-header-line {
    display: none;
  }
}
.editor-header-line{
  opacity: 0.5;
  margin: 0 8px;
}
</style>
